import { lazy, Suspense } from "react";
import i18next from "i18next";
import {
  MdInventory,
  MdAttachMoney,
  MdAccountBalanceWallet,
  MdPeople,
  MdAssessment,
  MdOutlineQueryStats,
  MdOutlineSystemUpdateAlt,
  MdListAlt,
  MdSettings,
  MdLock,
  MdPersonAdd,
  MdGroups,
  MdBusiness,
  MdVpnKey,
  MdChat,
  MdSearch,
  MdAdd,
  MdPictureAsPdf,
  MdHome,
  MdPerson,
  MdList,
  MdFlag,
  MdAssignment,
  MdEmail,
  MdPayment,
  MdWindow,
  MdSms,
  MdWeb,
  MdViewAgenda,
  MdShowChart,
} from "react-icons/md";
import Dashboard from "views/Dashboard.js";
import AddInventory from "views/inventory/AddInventory";
import EvaluateInventory from "views/inventory/EvaluateInventory";
import SearchInventory from "views/inventory/SearchInventory";
import InventoryQueues from "views/inventory/InventoryQueues";
import TenantList from "views/tenant/TenantList";
import ApiPermissions from "views/settings/ApiPermissions/ApiPermissions";
import DailyRecap from "views/reports/DailyRecap";
import Reports from "views/reports/Reports";
import ReportAdd from "views/reports/ReportAdd";
import StandardTaxReports from "views/reports/StandardTaxReports";
import TxDealersInventoryTaxStatement from "views/reports/TxDealersInventoryTaxStatement";
import TxSellerFinancedSalesTaxForm from "views/reports/TxSellerFinancedSalesTaxForm";
import Institution from "views/settings/Institution/Institution";
import Roles from "views/settings/Roles/Roles";
import RolesAdd from "views/settings/Roles/RolesAdd";
import Channels from "views/settings/Channels/Channels";
import ChannelsAdd from "views/settings/Channels/ChannelsAdd";
import QueueManager from "views/settings/QueueManager";
import QueueLayouts from "views/settings/QueueLayouts";
import Flags from "views/settings/Flags/Flags";
import CommunicationTemplates from "views/settings/CommunicationTemplates";
import CommunicationTemplatesAdd from "views/settings/CommunicationTemplatesAdd";
import DealerProfile from "views/settings/DealerProfile";
import LocationAdd from "views/settings/Locations/LocationAdd";
import FlagsAdd from "views/settings/Flags/FlagsAdd.js";
import InventoryStatusAdd from "views/inventory/status/InventoryStatusAdd";
import InventoryDetail from "views/inventory/InventoryDetail";
import SecureMediaViewer from "views/media/SecureMediaViewer";
import AddInventoryDetail from "views/inventory/AddInventoryDetail";
import TenantAdd from "views/tenant/TenantAdd";
import TenantEdit from "views/tenant/TenantEdit";
import EquipmentAdd from "views/settings/Equipments/EquipmentAdd";
import Register from "views/Auth/Register/Register";
import Login from "views/Auth/Login/Login";
import User from "views/users/User";
import UserDetail from "views/users/UserDetail";
import TenantUserDetail from "views/tenant/TenantUserDetail";
import TenantUserAdd from "views/tenant/TenantUserAdd";
import UserAdd from "views/users/UserAdd";
import UserSuperAdmin from "views/super-admin/users/User.js";
import UserAddSuperAdmin from "views/super-admin/users/UserAdd.js";
import UserDetailSuperAdmin from "views/super-admin/users/UserDetail.js";
import InventoriesSuperAdmin from "views/super-admin/inventories/Inventories.js";
import DealsSuperAdmin from "views/super-admin/deals/Deals.js";
import AccountsSuperAdmin from "views/super-admin/accounts/Accounts.js";
import LogsSuperAdmin from "views/super-admin/logs/Logs.js";
import AppIntegrationSuperAdmin from "views/super-admin/app-integration/AppIntegration.js";
import InventoryParameters from "views/settings/InventoryParameters";
import AccountParameters from "views/settings/AccountParameters";
import SearchDeals from "views/deals/SearchDeals";
import Accounts from "views/accounts/Accounts";
import FigureADeal from "views/deals/FigureADeal";
import AddRetailDeal from "views/deals/AddRetailDeal";
import WholesaleDeals from "views/deals/WholesaleDeals";
import DealsQueues from "views/deals/DealsQueues";
import DealsDetail from "views/deals/DealsDetail";
import Customers from "views/customers/Customers";
import CustomerDetail from "views/customers/CustomerDetail";
import AccountsDetail from "views/accounts/AccountsDetail";
import AddAccount from "views/accounts/AddAccount";
import Portfolios from "views/settings/Portfolios/Portfolios";
import AddPortfolios from "views/settings/Portfolios/AddPortfolios";
import EditPortfolios from "views/settings/Portfolios/EditPortfolios";
import ChangePassword from "views/change-password/ChangePassword";
import Logs from "views/logs/Logs";
import PatchNotes from "views/system/PatchNotes";
import CustomerAdd from "views/customers/CustomerAdd";
import { CiCalculator1, CiSettings, CiWallet } from "react-icons/ci";
import { IoIosHome } from "react-icons/io";
import { IoCarOutline, IoChatbubblesOutline } from "react-icons/io5";
import { LuUsers, LuGlobe } from "react-icons/lu";
import {
  WEB_MAKER_EXECUTABLE_ROUTE_PATH,
  WEB_MAKER_NAVIGATION_ITEMS,
} from "views/web-maker/utils/webMakerNavigation";
import InventoryWebsite from "views/web-maker/InventoryWebsite";
import InventoryWebsiteDetail from "views/web-maker/InventoryWebsiteDetail";
import WebsitePerformance from "views/web-maker/WebsitePerformance";
import { RiCarFill } from "react-icons/ri";
import { RiCalculatorFill } from "react-icons/ri";
import CustomerDetailQR from "views/customers/CustomerDetailQR";
import Documents from "views/settings/Documents/Documents";
import EmailSettings from "views/settings/EmailSettings";
import PaymentSettings from "views/settings/PaymentSettings";
import BulkMessagingSend from "views/communications/BulkMessagingSend";
import SidebarAppearance from "views/settings/SidebarAppearance";
import Chat from "views/chat/Chat";
import WorksheetPrintView from "views/components/Deals/WorksheetPrintView";

const WebMaker = lazy(() => import("views/web-maker/WebMaker"));

const WEB_MAKER_NAV_PRESENTATION = {
  pages: { mini: "PG", miniIcon: <MdList size={16} /> },
  preview: { mini: "PV", miniIcon: <MdWeb size={16} /> },
  settings: { mini: "ST", miniIcon: <MdSettings size={16} /> },
  theme: { mini: "TH", miniIcon: <MdWindow size={16} /> },
  sections: { mini: "SC", miniIcon: <MdViewAgenda size={16} /> },
  catalog: { mini: "CP", miniIcon: <MdListAlt size={16} /> },
};

const buildRoutes = () => [
  {
    collapse: false,
    path: "/patch-notes",
    name: i18next.t("routes.patchNotes"),
    state: "systemCollapse",
    component: (props) => {
      const activeColor = props.activeColor || "blue";
      return <PatchNotes {...props} activeColor={activeColor} />;
    },
    layout: "/admin",
    invisible: true,
  },
  {
    collapse: false,
    path: "/sidebar-appearance",
    name: i18next.t("routes.sidebarAppearance"),
    state: "systemCollapse",
    component: (props) => {
      const activeColor = props.activeColor || "tertiary";
      return (
        <SidebarAppearance
          activeColor={activeColor}
          handleActiveClick={props.handleActiveClick}
          account={props.account}
          isLightTheme={props.isLightTheme}
          sidebarRoutes={props.sidebarRoutes}
          sidebarMini={props.sidebarMini}
          sidebarMiniLayoutReady={props.sidebarMiniLayoutReady ?? true}
          shellThemeVariant={props.shellThemeVariant}
        />
      );
    },
    layout: "/admin",
    invisible: true,
  },
  {
    path: "/dashboard",
    name: i18next.t("routes.home"),
    icon: <IoIosHome size={25} />,
    component: (props) => {
      // Admin layout'undan gelen activeColor'ı al
      const activeColor = props.activeColor || "blue";
      return <Dashboard {...props} activeColor={activeColor} />;
    },
    layout: "/admin",
  },

  {
    collapse: false,
    path: "/tenant",
    name: i18next.t("routes.tenant"),
    icon: <MdBusiness size={20} />,
    component: (props) => {
      // Admin layout'undan gelen activeColor'ı al
      const activeColor = props.activeColor || "blue";
      return <TenantList {...props} activeColor={activeColor} />;
    },
    layout: "/admin",
    permission: "super_admin_tenant_read",
  },
  {
    collapse: false,
    path: "/super-admin/users",
    name: i18next.t("routes.users"),
    icon: <MdGroups size={25} />,
    state: "superAdminUsersCollapse",
    component: (props) => {
      // Admin layout'undan gelen activeColor'ı al
      const activeColor = props.activeColor || "blue";
      return <UserSuperAdmin {...props} activeColor={activeColor} />;
    },
    layout: "/admin",
    invisible: false,
    permission: "super_admin_tenant_read",
  },
  {
    collapse: false,
    path: "/super-admin/inventories",
    name: i18next.t("routes.inventory"),
    icon: <MdInventory size={25} />,
    component: (props) => {
      // Admin layout'undan gelen activeColor'ı al
      const activeColor = props.activeColor || "blue";
      return <InventoriesSuperAdmin {...props} activeColor={activeColor} />;
    },
    layout: "/admin",
    invisible: false,
    permission: "super_admin_tenant_read",
  },
  {
    collapse: false,
    path: "/super-admin/deals",
    name: i18next.t("routes.deals"),
    icon: <MdAttachMoney size={25} />,
    component: (props) => {
      const activeColor = props.activeColor || "blue";
      return <DealsSuperAdmin {...props} activeColor={activeColor} />;
    },
    layout: "/admin",
    invisible: false,
    permission: "super_admin_tenant_read",
  },
  {
    collapse: false,
    path: "/super-admin/accounts",
    name: i18next.t("routes.accounts"),
    icon: <MdAccountBalanceWallet size={25} />,
    iconTheme2: <CiWallet size={25} />,
    component: (props) => {
      const activeColor = props.activeColor || "blue";
      return <AccountsSuperAdmin {...props} activeColor={activeColor} />;
    },
    layout: "/admin",
    invisible: false,
    permission: "super_admin_tenant_read",
  },
  {
    collapse: false,
    path: "/super-admin/logs",
    name: i18next.t("routes.saLogs"),
    icon: <MdListAlt size={25} />,
    iconTheme2: <MdOutlineSystemUpdateAlt size={25} />,
    component: (props) => {
      const activeColor = props.activeColor || "blue";
      return <LogsSuperAdmin {...props} activeColor={activeColor} />;
    },
    layout: "/admin",
    invisible: false,
    permission: "super_admin_tenant_read",
  },
  {
    collapse: false,
    path: "/super-admin/app-integration",
    name: i18next.t("routes.appIntegration"),
    icon: <MdVpnKey size={25} />,
    component: (props) => {
      const activeColor = props.activeColor || "blue";
      return <AppIntegrationSuperAdmin {...props} activeColor={activeColor} />;
    },
    layout: "/admin",
    invisible: false,
    permission: "super_admin_tenant_read",
  },
  {
    path: "/chat",
    name: "DealerAI",
    icon: <MdChat size={25} />,
    component: (props) => {
      const activeColor = props.activeColor || "blue";
      return <Chat {...props} activeColor={activeColor} />;
    },
    layout: "/admin",
    permission: "super_admin_tenant_read",
  },
  {
    path: "/super-admin/users/add",
    name: i18next.t("routes.addUser"),
    icon: <MdPersonAdd size={20} />,
    component: (props) => {
      // Admin layout'undan gelen activeColor'ı al
      const activeColor = props.activeColor || "blue";
      return <UserAddSuperAdmin {...props} activeColor={activeColor} />;
    },
    layout: "/admin",
    invisible: true,
    permission: "super_admin_tenant_read",
  },
  {
    path: "/super-admin/users/:id",
    name: i18next.t("routes.userDetail"),
    component: (props) => {
      // Admin layout'undan gelen activeColor'ı al
      const activeColor = props.activeColor || "blue";
      return <UserDetailSuperAdmin {...props} activeColor={activeColor} />;
    },
    layout: "/admin",
    invisible: true,
    permission: "super_admin_tenant_read",
  },
  {
    collapse: true,
    name: i18next.t("routes.inventory"),
    icon: <RiCarFill size={25} />,
    iconTheme2: <IoCarOutline size={22} />,
    state: "inventoryCollapse",
    permission: "inventory_read",
    views: [
      {
        path: "/add-inventory",
        name: i18next.t("routes.addInventory"),
        layout: "/admin",
        mini: "AI",
        miniIcon: <MdAdd size={16} />,
        component: (props) => {
          // Admin layout'undan gelen activeColor'ı al
          const activeColor = props.activeColor || "blue";
          return <AddInventory {...props} activeColor={activeColor} />;
        },
        permission: "inventory_add",
      },
      {
        invisible: true,
        path: "/evaluate-inventory",
        name: i18next.t("routes.evaluateInventory"),
        mini: "EI",
        component: <EvaluateInventory />,
        permission: "inventory_evaluate_read",
        layout: "/admin",
      },
      {
        path: "/search-inventory",
        sidebarActiveWhenPaths: ["/inventory/:id"],
        name: i18next.t("routes.searchInventory"),
        mini: "SI",
        miniIcon: <MdSearch size={16} />,
        component: (props) => {
          // Admin layout'undan gelen activeColor'ı al
          const activeColor = props.activeColor || "blue";
          return <SearchInventory {...props} activeColor={activeColor} />;
        },
        permission: "inventory_search_read",
        layout: "/admin",
      },
      {
        path: "/media/uploads/*",
        name: "Media",
        component: <SecureMediaViewer />,
        layout: "/admin",
        invisible: true,
      },
      {
        path: "/inventory/uploads/*",
        name: "Media",
        component: <SecureMediaViewer />,
        layout: "/admin",
        invisible: true,
      },
      {
        path: "/inventory/:id",
        name: i18next.t("routes.inventoryDetail"),
        component: (props) => {
          // Admin layout'undan gelen activeColor'ı al
          const activeColor = props.activeColor || "blue";
          return <InventoryDetail {...props} activeColor={activeColor} />;
        },
        layout: "/admin",
        invisible: true,
      },
      {
        invisible: true,

        path: "/inventory-queues",
        name: i18next.t("routes.queues"),
        mini: "IQ",
        component: <InventoryQueues />,
        layout: "/admin",
        permission: "inventory_queues_read",
      },
      {
        path: "/inventory-status/add",
        name: i18next.t("routes.addStatus"),
        mini: "AS",
        component: (props) => {
          // Admin layout'undan gelen activeColor'ı al
          const activeColor = props.activeColor || "blue";
          return <InventoryStatusAdd {...props} activeColor={activeColor} />;
        },
        layout: "/admin",
        invisible: true,
        permission: "inventory_parameters_status_add",
      },
      {
        path: "/inventory-status/edit/:id",
        name: i18next.t("routes.editStatus"),
        mini: "ES",
        component: (props) => {
          // Admin layout'undan gelen activeColor'ı al
          const activeColor = props.activeColor || "blue";
          return <InventoryStatusAdd {...props} activeColor={activeColor} />;
        },
        layout: "/admin",
        invisible: true,
        permission: "inventory_parameters_status_edit",
      },
      {
        path: "/add-inventory-detail",
        name: i18next.t("routes.addInventoryDetail"),
        mini: "AID",
        component: <AddInventoryDetail />,
        layout: "/admin",
        invisible: true,
        permission: "inventory_add",
      },
    ],
  },
  {
    collapse: true,
    name: i18next.t("routes.deals"),
    icon: <RiCalculatorFill size={25} />,
    iconTheme2: <CiCalculator1 size={25} />,
    state: "dealsCollapse",
    permission: "deals_read",
    views: [
      {
        path: "/deals",
        name: i18next.t("routes.searchDeals"),
        mini: "SD",
        miniIcon: <MdSearch size={16} />,
        component: (props) => {
          // Admin layout'undan gelen activeColor'ı al
          const activeColor = props.activeColor || "blue";
          return <SearchDeals {...props} activeColor={activeColor} />;
        },
        layout: "/admin",
        permission: "deals_read",
      },
      {
        path: "/deals/:id",
        name: i18next.t("routes.dealDetail"),
        mini: "DD",
        invisible: true,
        component: (props) => {
          const activeColor = props.activeColor || "blue";
          return <DealsDetail {...props} activeColor={activeColor} />;
        },
        layout: "/admin",
        permission: "deals_read",
      },
      {
        path: "/figure-a-deal",
        name: i18next.t("routes.figureADeal"),
        mini: "FD",
        component: (props) => {
          // Admin layout'undan gelen activeColor'ı al
          const activeColor = props.activeColor || "blue";
          return <FigureADeal {...props} activeColor={activeColor} />;
        },
        layout: "/admin",
        permission: "deals_figure_read",
      },
      {
        path: "/add-retail-deal",
        name: i18next.t("routes.addRetailDeal"),
        mini: "RD",
        miniIcon: <MdAdd size={16} />,
        component: (props) => {
          // Admin layout'undan gelen activeColor'ı al
          const activeColor = props.activeColor || "blue";
          return <AddRetailDeal {...props} activeColor={activeColor} />;
        },
        layout: "/admin",
        permission: "deals_retail_add",
      },
      {
        path: "/wholesale-deals",
        name: i18next.t("routes.wholesaleDeals"),
        mini: "WD",
        miniIcon: <MdList size={16} />,
        component: (props) => {
          // Admin layout'undan gelen activeColor'ı al
          const activeColor = props.activeColor || "blue";
          return <WholesaleDeals {...props} activeColor={activeColor} />;
        },
        layout: "/admin",
        permission: "deals_wholesale_add",
      },
      {
        path: "/deals-queues",
        name: i18next.t("routes.queues"),
        mini: "DQ",
        component: (props) => {
          // Admin layout'undan gelen activeColor'ı al
          const activeColor = props.activeColor || "blue";
          return <DealsQueues {...props} activeColor={activeColor} />;
        },
        layout: "/admin",
        permission: "deals_queues_read",
      },
    ],
  },
  {
    collapse: true,
    name: i18next.t("routes.accounts"),
    icon: <MdAccountBalanceWallet size={25} />,
    iconTheme2: <CiWallet size={20} />,
    state: "accountsCollapse",
    permission: "accounts_read",
    views: [
      {
        path: "/accounts",
        name: i18next.t("routes.accounts"),
        mini: "AC",
        miniIcon: <MdSearch size={16} />,
        component: (props) => {
          const activeColor = props.activeColor || "blue";
          return <Accounts {...props} activeColor={activeColor} />;
        },
        layout: "/admin",
        permission: "accounts_read",
      },
      {
        path: "/add-account",
        name: i18next.t("routes.addAccount"),
        mini: "AA",
        miniIcon: <MdAdd size={16} />,
        component: (props) => {
          const activeColor = props.activeColor || "blue";
          return <AddAccount {...props} activeColor={activeColor} />;
        },
        layout: "/admin",
        permission: "accounts_add",
      },
      {
        path: "/accounts/:id",
        name: i18next.t("routes.accountDetail"),
        mini: "AD",
        invisible: true,
        component: (props) => {
          const activeColor = props.activeColor || "blue";
          return <AccountsDetail {...props} activeColor={activeColor} />;
        },
        layout: "/admin",
        permission: "accounts_read",
      },
    ],
  },
  {
    collapse: false,
    path: "/customers",
    name: i18next.t("routes.customers"),
    icon: <MdPeople size={25} />,
    iconTheme2: <LuUsers size={20} className="sidebar-shell-theme-2-outline-svg" />,
    state: "customersCollapse",
    component: (props) => {
      // Admin layout'undan gelen activeColor'ı al
      const activeColor = props.activeColor || "blue";
      return <Customers {...props} activeColor={activeColor} />;
    },
    layout: "/admin",
    invisible: false,
    permission: "customers_read",
  },
  {
    path: "/customers/add",
    name: i18next.t("routes.addCustomer"),
    component: (props) => {
      const activeColor = props.activeColor || "blue";
      return <CustomerAdd {...props} activeColor={activeColor} />;
    },
    layout: "/admin",
    invisible: true,
    permission: "customers_add",
  },
  {
    path: "/customers/:id",
    name: i18next.t("routes.customerDetail"),
    component: (props) => {
      const activeColor = props.activeColor || "blue";
      return <CustomerDetail {...props} activeColor={activeColor} />;
    },
    layout: "/admin",
    invisible: true,
    permission: "customers_read",
  },
  {
    path: "/tenant/add/:id?",
    name: i18next.t("routes.addTenant"),
    component: (props) => {
      // Admin layout'undan gelen activeColor'ı al
      const activeColor = props.activeColor || "blue";
      return <TenantAdd {...props} activeColor={activeColor} />;
    },
    layout: "/admin",
    invisible: true,
    permission: "super_admin_tenant_read",
  },
  {
    path: "/tenant/edit/:id",
    name: i18next.t("routes.tenantDetail"),
    component: (props) => {
      // Admin layout'undan gelen activeColor'ı al
      const activeColor = props.activeColor || "blue";
      return <TenantEdit {...props} activeColor={activeColor} account={props.account} />;
    },
    layout: "/admin",
    invisible: true,
    permission: "super_admin_tenant_read",
  },
  {
    collapse: true,
    name: i18next.t("routes.reports"),
    icon: <MdAssessment size={25} />,
    iconTheme2: <MdOutlineQueryStats size={21} />,
    state: "reportsCollapse",
    permission: "reports_read",
    views: [
      {
        path: "/reports",
        name: i18next.t("routes.reports"),
        mini: "R",
        miniIcon: <MdList size={16} />,
        component: (props) => {
          const activeColor = props.activeColor || "blue";
          return <Reports {...props} activeColor={activeColor} />;
        },
        layout: "/admin",
        permission: "reports_read",
      },
      {
        path: "/reports/add",
        name: i18next.t("routes.addReport"),
        mini: "AR",
        component: (props) => {
          const activeColor = props.activeColor || "blue";
          return <ReportAdd {...props} activeColor={activeColor} />;
        },
        layout: "/admin",
        invisible: true,
        permission: "reports_add",
      },
      {
        path: "/reports/:id",
        name: i18next.t("routes.editReport"),
        mini: "ER",
        component: (props) => {
          const activeColor = props.activeColor || "blue";
          return <ReportAdd {...props} activeColor={activeColor} />;
        },
        layout: "/admin",
        invisible: true,
        permission: "reports_read",
      },

      {
        path: "/e-cabinet",
        name: i18next.t("routes.eCabinet"),
        mini: "EC",
        component: <eCabinet />,
        invisible: true,
        layout: "/admin",
      },
      {
        path: "/daily-recap",
        name: i18next.t("routes.dailyRecap"),
        mini: "DR",
        miniIcon: <MdAttachMoney size={16} />,
        component: (props) => {
          const activeColor = props.activeColor || "blue";
          return <DailyRecap {...props} activeColor={activeColor} />;
        },
        layout: "/admin",
        permission: "daily_recap_read",
      },
      {
        path: "/standard-tax-reports",
        name: i18next.t("routes.standardTaxReports"),
        mini: "STR",
        miniIcon: <MdPictureAsPdf size={16} />,
        component: (props) => <StandardTaxReports {...props} />,
        layout: "/admin",
        permission: "standard_tax_reports_read",
      },
      {
        path: "/standard-tax-reports/vit-statement",
        name: "TX - Dealers Inventory Tax Statement (VIT)",
        mini: "VIT",
        component: (props) => <TxDealersInventoryTaxStatement {...props} />,
        layout: "/admin",
        invisible: true,
        permission: "standard_tax_reports_read",
      },
      {
        path: "/standard-tax-reports/seller-financed-sales-tax-form",
        name: "TX - Motor Vehicle Seller-Financed Sales Tax Form",
        mini: "SFT",
        component: (props) => <TxSellerFinancedSalesTaxForm {...props} />,
        layout: "/admin",
        invisible: true,
        permission: "standard_tax_reports_read",
      },
    ],
  },
  {
    collapse: false,
    path: "/logs",
    name: i18next.t("routes.logs"),
    icon: <MdListAlt size={25} />,
    iconTheme2: <MdOutlineSystemUpdateAlt size={20} />,
    state: "logsCollapse",
    component: (props) => {
      const activeColor = props.activeColor || "blue";
      return <Logs {...props} activeColor={activeColor} />;
    },
    layout: "/admin",
    invisible: false,
    permission: "logs_read",
    hideForSuperAdmin: true,
  },
  {
    collapse: true,
    name: i18next.t("routes.settings"),
    icon: <MdSettings size={25} />,
    iconTheme2: <CiSettings size={20} />,
    state: "settingsCollapse",
    permission: "settings_read",
    views: [
      {
        path: "/institution",
        name: i18next.t("routes.institution"),
        mini: "I",
        miniIcon: <MdHome size={16} />,
        component: (props) => {
          // Admin layout'undan gelen activeColor'ı al
          const activeColor = props.activeColor || "blue";
          return <Institution {...props} activeColor={activeColor} />;
        },
        layout: "/admin",
        permission: "settings_institution_read",
      },
      {
        path: "/roles",
        name: i18next.t("routes.roles"),
        mini: "R",
        miniIcon: <MdPerson size={16} />,
        component: (props) => {
          // Admin layout'undan gelen activeColor'ı al
          const activeColor = props.activeColor || "blue";
          return <Roles {...props} activeColor={activeColor} />;
        },
        layout: "/admin",
        permission: "roles_read",
      },

      {
        path: "/roles/add",
        name: i18next.t("routes.addRole"),
        component: (props) => {
          // Admin layout'undan gelen activeColor'ı al
          const activeColor = props.activeColor || "blue";
          return <RolesAdd {...props} activeColor={activeColor} />;
        },
        layout: "/admin",
        invisible: true,
        permission: "roles_add",
      },
      {
        path: "/roles/edit/:id",
        name: i18next.t("routes.editRole"),
        component: (props) => {
          // Admin layout'undan gelen activeColor'ı al
          const activeColor = props.activeColor || "blue";
          return <RolesAdd {...props} activeColor={activeColor} />;
        },
        layout: "/admin",
        invisible: true,
        permission: "roles_edit",
      },
      {
        path: "/portfolios",
        name: i18next.t("routes.portfolios"),
        mini: "P",
        miniIcon: <MdPeople size={16} />,
        component: (props) => {
          const activeColor = props.activeColor || "blue";
          return <Portfolios {...props} activeColor={activeColor} />;
        },
        layout: "/admin",
        permission: "settings_portfolios_read",
      },
      {
        path: "/portfolios/add",
        name: i18next.t("routes.addPortfolio"),
        mini: "AP",
        component: (props) => {
          const activeColor = props.activeColor || "blue";
          return <AddPortfolios {...props} activeColor={activeColor} />;
        },
        layout: "/admin",
        invisible: true,
        permission: "settings_portfolios_add",
      },
      {
        path: "/portfolios/:id",
        name: i18next.t("routes.editPortfolio"),
        mini: "EP",
        component: (props) => {
          const activeColor = props.activeColor || "blue";
          return <EditPortfolios {...props} activeColor={activeColor} />;
        },
        layout: "/admin",
        invisible: true,
        permission: "settings_portfolios_edit",
      },
      {
        path: "/channels",
        name: i18next.t("routes.channels"),
        mini: "C",
        miniIcon: <MdList size={16} />,
        component: (props) => {
          // Admin layout'undan gelen activeColor'ı al
          const activeColor = props.activeColor || "blue";
          return <Channels {...props} activeColor={activeColor} />;
        },
        layout: "/admin",
        permission: "channels_read",
      },
      {
        path: "/channels/add",
        name: i18next.t("routes.addChannel"),
        mini: "AC",
        component: (props) => {
          // Admin layout'undan gelen activeColor'ı al
          const activeColor = props.activeColor || "blue";
          return <ChannelsAdd {...props} activeColor={activeColor} />;
        },
        layout: "/admin",
        invisible: true,
        permission: "channels_add",
      },
      {
        path: "/channels/:id",
        name: i18next.t("routes.channelDetail"),
        mini: "EC",
        component: (props) => {
          // Admin layout'undan gelen activeColor'ı al
          const activeColor = props.activeColor || "blue";
          return <ChannelsAdd {...props} activeColor={activeColor} />;
        },
        layout: "/admin",
        invisible: true,
        permission: "channels_edit",
      },
      {
        collapse: false,
        path: "/api-permissions",
        mini: "API",
        miniIcon: <MdVpnKey size={16} />,
        name: i18next.t("routes.apiPermissions"),
        component: (props) => {
          // Admin layout'undan gelen activeColor'ı al
          const activeColor = props.activeColor || "blue";
          return <ApiPermissions {...props} activeColor={activeColor} />;
        },
        layout: "/admin",
        permission: "api_permissions_view",
      },
      {
        path: "/documents",
        name: i18next.t("routes.documents"),
        mini: "D",
        miniIcon: <MdFlag size={16} />,
        component: (props) => {
          // Admin layout'undan gelen activeColor'ı al
          const activeColor = props.activeColor || "blue";
          return <Documents {...props} activeColor={activeColor} />;
        },
        layout: "/admin",
        permission: "documents_read",
      },

      {
        path: "/queue-manager",
        name: i18next.t("routes.queueManager"),
        mini: "QM",
        component: <QueueManager />,
        layout: "/admin",
        permission: "settings_queue_manager_read",
      },
      {
        path: "/queue-layouts",
        name: i18next.t("routes.queueLayouts"),
        mini: "QL",
        component: <QueueLayouts />,
        layout: "/admin",
        permission: "settings_queue_layouts_read",
      },
      {
        path: "/flags",
        name: i18next.t("routes.flags"),
        mini: "F",
        miniIcon: <MdFlag size={16} />,
        component: (props) => {
          // Admin layout'undan gelen activeColor'ı al
          const activeColor = props.activeColor || "blue";
          return <Flags {...props} activeColor={activeColor} />;
        },
        layout: "/admin",
        permission: "flags_read",
      },
      {
        path: "/flags/add",
        name: i18next.t("routes.addFlag"),
        mini: "AF",
        component: (props) => {
          // Admin layout'undan gelen activeColor'ı al
          const activeColor = props.activeColor || "blue";
          return <FlagsAdd {...props} activeColor={activeColor} />;
        },
        layout: "/admin",
        invisible: true,
        permission: "flags_add",
      },
      {
        path: "/flags/edit/:id",
        name: i18next.t("routes.editFlag"),
        mini: "EF",
        component: (props) => {
          // Admin layout'undan gelen activeColor'ı al
          const activeColor = props.activeColor || "blue";
          return <FlagsAdd {...props} activeColor={activeColor} />;
        },
        layout: "/admin",
        invisible: true,
        permission: "flags_edit",
      },
      {
        path: "/dealer-profile",
        name: i18next.t("routes.dealerProfile"),
        mini: "DP",
        component: <DealerProfile />,
        layout: "/admin",
        permission: "settings_dealer_profile_read",
      },
      {
        path: "/inventory-parameters",
        sidebarActiveWhenPaths: [
          "/locations/add",
          "/locations/edit/:id",
          "/equipments/add",
          "/equipments/edit/:id",
        ],
        name: i18next.t("routes.inventoryParameters"),
        mini: "IP",
        miniIcon: <MdAssignment size={16} />,
        component: (props) => {
          // Admin layout'undan gelen activeColor'ı al
          const activeColor = props.activeColor || "blue";
          return <InventoryParameters {...props} activeColor={activeColor} />;
        },
        layout: "/admin",
        permission: "inventory_parameters_read",
      },
      {
        path: "/email-settings",
        name: i18next.t("routes.emailSettings"),
        mini: "ES",
        miniIcon: <MdEmail size={16} />,
        component: (props) => {
          const activeColor = props.activeColor || "blue";
          return <EmailSettings {...props} activeColor={activeColor} />;
        },
        layout: "/admin",
        permission: "settings_email_read",
      },
      {
        path: "/payment-settings",
        name: i18next.t("routes.paymentSettings"),
        mini: "PS",
        miniIcon: <MdPayment size={16} />,
        component: (props) => {
          const activeColor = props.activeColor || "blue";
          return <PaymentSettings {...props} activeColor={activeColor} />;
        },
        layout: "/admin",
        permission: "settings_payment_read",
      },
      {
        path: "/account-parameters",
        name: i18next.t("routes.accountParameters"),
        mini: "AP",
        miniIcon: <MdWindow size={16} />,
        component: (props) => {
          // Admin layout'undan gelen activeColor'ı al
          const activeColor = props.activeColor || "blue";
          return <AccountParameters {...props} activeColor={activeColor} />;
        },
        layout: "/admin",
        permission: "settings_account_parameters_read",
      },
      {
        path: "/communication-templates",
        name: i18next.t("routes.communicationTemplates"),
        mini: "CT",
        miniIcon: <MdWindow size={16} />,
        component: (props) => {
          const activeColor = props.activeColor || "blue";
          return <CommunicationTemplates {...props} activeColor={activeColor} />;
        },
        layout: "/admin",
      },
      {
        path: "/communication-templates/add",
        name: i18next.t("routes.addCommunicationTemplate"),
        mini: "ACT",
        component: (props) => {
          const activeColor = props.activeColor || "blue";
          return <CommunicationTemplatesAdd {...props} activeColor={activeColor} />;
        },
        layout: "/admin",
        invisible: true,
      },
      {
        path: "/communication-templates/:id",
        name: i18next.t("routes.communicationTemplateDetail"),
        mini: "CTD",
        component: (props) => {
          const activeColor = props.activeColor || "blue";
          return <CommunicationTemplatesAdd {...props} activeColor={activeColor} />;
        },
        layout: "/admin",
        invisible: true,
      },
    ],
  },
  {
    collapse: true,
    name: i18next.t("routes.communications"),
    icon: <MdChat size={25} />,
    iconTheme2: <IoChatbubblesOutline size={22} />,
    state: "communicationsCollapse",
    requiresCommunicationsView: true,
    views: [
      {
        path: "/communications/send-mail",
        name: i18next.t("routes.sendBulkMail"),
        mini: "EM",
        miniIcon: <MdEmail size={16} />,
        component: (props) => {
          const activeColor = props.activeColor || "blue";
          return <BulkMessagingSend key="bulk-mail" {...props} activeColor={activeColor} channel="mail" />;
        },
        layout: "/admin",
        requiresCommunicationsView: true,
      },
      {
        path: "/communications/send-sms",
        name: i18next.t("routes.sendBulkSms"),
        mini: "SM",
        miniIcon: <MdSms size={16} />,
        component: (props) => {
          const activeColor = props.activeColor || "blue";
          return <BulkMessagingSend key="bulk-sms" {...props} activeColor={activeColor} channel="sms" />;
        },
        layout: "/admin",
        requiresCommunicationsView: true,
      },
    ],
  },
  {
    collapse: true,
    name: i18next.t("routes.authentication"),
    invisible: true,
    icon: <MdLock size={20} />,
    state: "authCollapse",
    views: [
      {
        path: "/login",
        name: i18next.t("routes.login"),
        mini: "L",
        component: (props) => {
          // Admin layout'undan gelen activeColor'ı al
          const activeColor = props.activeColor || "blue";
          return <Login {...props} activeColor={activeColor} />;
        },
        layout: "/auth",
      },
      {
        path: "/register",
        name: i18next.t("routes.register"),
        mini: "R",
        component: (props) => {
          // Admin layout'undan gelen activeColor'ı al
          const activeColor = props.activeColor || "blue";
          return <Register {...props} activeColor={activeColor} />;
        },
        layout: "/auth",
      },
    ],
  },
  {
    path: "/change-password",
    name: i18next.t("routes.changePassword"),
    component: (props) => {
      const activeColor = props.activeColor || "blue";
      return (
        <ChangePassword
          activeColor={activeColor}
          isLightTheme={props.isLightTheme}
          shellThemeVariant={props.shellThemeVariant}
        />
      );
    },
    invisible: true,
    layout: "/admin",
  },
  {
    path: "/users/add",
    name: i18next.t("routes.addUser"),
    icon: <MdPersonAdd size={20} />,
    component: (props) => {
      // Admin layout'undan gelen activeColor'ı al
      const activeColor = props.activeColor || "blue";
      return <UserAdd {...props} activeColor={activeColor} />;
    },
    layout: "/admin",
    invisible: true,
    permission: "users_add",
  },
  {
    path: "/users/:id",
    name: i18next.t("routes.userDetail"),
    component: (props) => {
      // Admin layout'undan gelen activeColor'ı al
      const activeColor = props.activeColor || "blue";
      return <UserDetail {...props} activeColor={activeColor} />;
    },
    layout: "/admin",
    invisible: true,
    permission: "users_edit",
  },
  {
    path: "/tenant/:tenantId/user/:id",
    name: i18next.t("routes.tenantUserDetail"),
    component: (props) => {
      // Admin layout'undan gelen activeColor'ı al
      const activeColor = props.activeColor || "blue";
      return <TenantUserDetail {...props} activeColor={activeColor} />;
    },
    layout: "/admin",
    invisible: true,
    permission: "super_admin_tenant_read",
  },
  {
    path: "/tenant/:tenantId/add-user",
    name: i18next.t("routes.tenantUserAdd"),
    component: (props) => {
      // Admin layout'undan gelen activeColor'ı al
      const activeColor = props.activeColor || "blue";
      return <TenantUserAdd {...props} activeColor={activeColor} />;
    },
    layout: "/admin",
    invisible: true,
    permission: "super_admin_tenant_read",
  },

  {
    collapse: true,
    name: i18next.t("routes.website"),
    icon: <MdWeb size={25} />,
    iconTheme2: <LuGlobe size={20} className="sidebar-shell-theme-2-outline-svg" />,
    state: "webMakerCollapse",
    permission: "web_maker",
    requiresWebEnabled: true,
    views: [
      {
        path: "/inventory-website",
        name: i18next.t("routes.inventoryWebsite"),
        mini: "IW",
        miniIcon: <MdSearch size={16} />,
        component: (props) => {
          const activeColor = props.activeColor || "blue";
          return <InventoryWebsite {...props} activeColor={activeColor} />;
        },
        layout: "/admin",
        permission: "web_maker",
        sidebarActiveWhenPaths: ["/inventory-website/:id"],
      },
      {
        path: "/website-performance",
        name: i18next.t("routes.websitePerformance"),
        mini: "PF",
        miniIcon: <MdShowChart size={16} />,
        component: (props) => {
          const activeColor = props.activeColor || "blue";
          return <WebsitePerformance {...props} activeColor={activeColor} />;
        },
        layout: "/admin",
        permission: "web_maker",
      },
      {
        path: "/inventory-website/:id",
        name: i18next.t("routes.inventoryWebsite"),
        component: (props) => {
          const activeColor = props.activeColor || "blue";
          return <InventoryWebsiteDetail {...props} activeColor={activeColor} />;
        },
        layout: "/admin",
        invisible: true,
        permission: "web_maker",
      },
      ...WEB_MAKER_NAVIGATION_ITEMS.map((item) => ({
        path: item.path,
        name: i18next.t(`webMaker.tabs.${item.page}`),
        ...WEB_MAKER_NAV_PRESENTATION[item.page],
        layout: "/admin",
        permission: "web_maker",
        navigationOnly: true,
        webMakerNavigation: true,
        superAdminOnly: item.superAdminOnly,
      })),
      {
        path: WEB_MAKER_EXECUTABLE_ROUTE_PATH,
        name: i18next.t("routes.webMaker"),
        component: (props) => {
          const activeColor = props.activeColor || "blue";
          return (
            <Suspense
              fallback={
                <div role="status" aria-live="polite">
                  {i18next.t("webMaker.loadingEditor")}
                </div>
              }
            >
              <WebMaker
                {...props}
                activeColor={activeColor}
                account={props.account}
                navigationMode
              />
            </Suspense>
          );
        },
        layout: "/admin",
        invisible: true,
        permission: "web_maker",
        webMakerRoute: true,
      },
    ],
  },
  {
    collapse: false,
    path: "/users",
    name: i18next.t("routes.users"),
    icon: <MdGroups size={25} />,
    state: "usersCollapse",
    component: (props) => {
      // Admin layout'undan gelen activeColor'ı al
      const activeColor = props.activeColor || "blue";
      return <User {...props} activeColor={activeColor} />;
    },
    layout: "/admin",
    invisible: false,
    permission: "users_read",
  },
  {
    path: "/locations/add",
    name: i18next.t("routes.addLocation"),
    component: (props) => {
      // Admin layout'undan gelen activeColor'ı al
      const activeColor = props.activeColor || "blue";
      return <LocationAdd {...props} activeColor={activeColor} />;
    },
    layout: "/admin",
    invisible: true,
    permission: "inventory_parameters_locations_add",
  },
  {
    path: "/locations/edit/:id",
    name: i18next.t("routes.editLocation"),
    component: (props) => {
      // Admin layout'undan gelen activeColor'ı al
      const activeColor = props.activeColor || "blue";
      return <LocationAdd {...props} activeColor={activeColor} />;
    },
    layout: "/admin",
    invisible: true,
    permission: "inventory_parameters_locations_edit",
  },
  {
    path: "/equipments/add",
    name: i18next.t("routes.addEquipment"),
    component: (props) => {
      // Admin layout'undan gelen activeColor'ı al
      const activeColor = props.activeColor || "blue";
      return <EquipmentAdd {...props} activeColor={activeColor} />;
    },
    layout: "/admin",
    invisible: true,
    permission: "inventory_parameters_equipments_add",
  },
  {
    path: "/equipments/edit/:id",
    name: i18next.t("routes.editEquipment"),
    component: (props) => {
      // Admin layout'undan gelen activeColor'ı al
      const activeColor = props.activeColor || "blue";
      return <EquipmentAdd {...props} activeColor={activeColor} />;
    },
    layout: "/admin",
    invisible: true,
    permission: "inventory_parameters_equipments_edit",
  },

  {
    path: "/customer/detail/:id",
    name: "Customer Detail",
    component: (props) => {
      const activeColor = props.activeColor || "blue";
      return <CustomerDetailQR {...props} activeColor={activeColor} />;
    },
    invisible: true,
    layout: "/auth",
  },
  {
    path: "/worksheets/:id/print",
    name: "Print Worksheet",
    component: () => <WorksheetPrintView />,
    invisible: true,
    layout: "/admin",
  },
];

export default buildRoutes;
